import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { clients, industries } from "../mock";
import useReveal from "../hooks/useReveal";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Real logo via Clearbit with graceful fallback to a clean wordmark.
function BrandLogo({ brand }) {
  const [failed, setFailed] = useState(!brand.domain);
  return (
    <div className="flex items-center justify-center h-12 px-6 min-w-[150px]">
      {!failed ? (
        <img
          src={`https://logo.clearbit.com/${brand.domain}?size=80`}
          alt={brand.name}
          onError={() => setFailed(true)}
          className="max-h-9 max-w-[130px] object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all"
        />
      ) : (
        <span className="text-[#1a2a6b] font-extrabold text-lg tracking-tight whitespace-nowrap opacity-80 hover:opacity-100 transition-opacity">
          {brand.name}
        </span>
      )}
    </div>
  );
}

export default function Clients() {
  const [ref, inView] = useReveal();
  const [active, setActive] = useState(null);
  const allBrands = clients.countries.flatMap((c) =>
    c.brands.map((b) => ({ ...b, code: c.code }))
  );
  const loop = [...allBrands, ...allBrands];

  return (
    <section id="clients" className="kse-section bg-white overflow-hidden">
      <div className="kse-container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="kse-eyebrow">
            {clients.eyebrow}
          </span>
          <h2 className="kse-h2">
            {clients.title}
          </h2>
          <p className="text-slate-500 mt-4">{clients.subtitle}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-14">
          {industries.map((ind) => (
            <div key={ind.name} className="rounded-2xl border border-slate-100 bg-slate-50/70 px-5 py-4">
              <div className="font-bold text-[#0f1a3d]">{ind.name}</div>
              <div className="text-slate-500 text-sm mt-0.5">{ind.note}</div>
              <div className="text-[#2b3aa0] text-xs font-semibold mt-2">e.g. {ind.example}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Logo marquee bar */}
      <div className="relative border-y border-slate-100 bg-slate-50/60 py-6 mb-16">
        <div className="kse-marquee items-center">
          {loop.map((b, i) => (
            <div key={b.name + i} className="flex items-center gap-2 shrink-0">
              <img
                src={`https://flagcdn.com/w40/${b.code}.png`}
                alt=""
                className="w-6 h-4 object-cover rounded-sm shadow-sm"
              />
              <BrandLogo brand={b} />
            </div>
          ))}
        </div>
      </div>

      {/* World map with markers */}
      <div ref={ref} className={`kse-container reveal ${inView ? "in-view" : ""}`}>
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2 rounded-3xl bg-[#0d1640] p-4 sm:p-8 shadow-xl">
            <ComposableMap
              projectionConfig={{ scale: 150 }}
              width={900}
              height={420}
              style={{ width: "100%", height: "auto" }}
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#1e2a63"
                      stroke="#2b3aa0"
                      strokeWidth={0.4}
                      style={{
                        default: { outline: "none" },
                        hover: { fill: "#2b3aa0", outline: "none" },
                        pressed: { outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>
              {clients.countries.map((c) => (
                <Marker key={c.code} coordinates={c.coords} onMouseEnter={() => setActive(c.region)} onMouseLeave={() => setActive(null)}>
                  <circle r={9} fill="#3a4dcc" opacity={0.35}>
                    <animate attributeName="r" values="7;14;7" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle r={4.5} fill="#ffffff" stroke="#3a4dcc" strokeWidth={2} />
                  {active === c.region && (
                    <text textAnchor="middle" y={-16} className="fill-white" style={{ fontSize: 13, fontWeight: 700 }}>
                      {c.region}
                    </text>
                  )}
                </Marker>
              ))}
            </ComposableMap>
          </div>

          {/* Region list */}
          <div className="space-y-3">
            {clients.countries.map((c) => (
              <div
                key={c.code}
                onMouseEnter={() => setActive(c.region)}
                onMouseLeave={() => setActive(null)}
                className={`flex items-center gap-3 rounded-2xl border p-4 transition-all ${
                  active === c.region ? "border-[#2b3aa0] bg-blue-50" : "border-slate-100 bg-white"
                }`}
              >
                <img src={`https://flagcdn.com/w40/${c.code}.png`} alt={c.region} className="w-8 h-6 object-cover rounded shadow-sm" />
                <div>
                  <div className="font-bold text-[#1a2a6b] text-sm">{c.region}</div>
                  <div className="text-slate-500 text-xs">{c.brands.map((b) => b.name).join(", ")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
